import { ref, type Ref } from 'vue';

/**
 * An intent handler. Vue invokes it with the native event when bound to a DOM
 * element; direct calls (tests, component `$emit`) may omit it.
 */
export type LazyHrefIntentHandler = (event?: Event) => void;

/**
 * Event handlers that reveal the href when a real user shows interaction
 * intent. Spread them onto the anchor element with `v-bind="intentHandlers"`.
 *
 * All of these events fire before the browser acts on the link:
 * - `pointerdown` fires before click, auxclick (middle-click) and contextmenu
 *   (right-click), so the default actions see a resolved href.
 * - `touchstart` fires before tap navigation and long-press menus.
 * - `focus` fires before keyboard activation (Enter), which restores native
 *   link behaviour for keyboard and screen reader users.
 * - `click` is a last-resort fallback (e.g. programmatic `.click()`): the
 *   anchor's default activation runs right after event dispatch, before
 *   Vue's queued (microtask) DOM patch could apply the reactive `href.value`,
 *   so the handler resolves the href synchronously on the event target.
 */
export interface LazyHrefIntentHandlers {
  onMouseenter: LazyHrefIntentHandler;
  onFocus: LazyHrefIntentHandler;
  onPointerdown: LazyHrefIntentHandler;
  onTouchstart: LazyHrefIntentHandler;
  onContextmenu: LazyHrefIntentHandler;
  onAuxclick: LazyHrefIntentHandler;
  onClick: LazyHrefIntentHandler;
}

export interface UseLazyHrefReturn {
  /** The link target, `undefined` until the user shows intent. */
  href: Ref<string | undefined>;
  /** Force the href to be resolved (rarely needed, exposed for tests). */
  reveal: () => void;
  /**
   * Reveal the href and, when an event is given, write it synchronously to
   * the event target so the anchor's default activation (navigation) resolves
   * it without waiting for Vue's async DOM patch. Use this in custom click
   * overrides so the event is not lost.
   */
  revealFromEvent: (event?: Event) => void;
  intentHandlers: LazyHrefIntentHandlers;
}

/**
 * Lazily reveals a link target only when a real user shows interaction intent.
 *
 * The URL getter lives in JS memory and is resolved on demand, so the target
 * never appears as an `href` in the SSR HTML or in the rendered DOM that
 * crawlers parse (Googlebot does not fire pointer, touch or focus events).
 * Users keep full link behaviour: right-click "Open in new tab",
 * middle-click, cmd/ctrl-click, long-press menus and keyboard activation.
 *
 * The getter is re-evaluated on every reveal, so reactive inputs (locale,
 * listing id) are always reflected.
 */
export function useLazyHref(getUrl: () => string): UseLazyHrefReturn {
  const href: Ref<string | undefined> = ref(undefined);

  const reveal = (): void => {
    href.value = getUrl();
  };

  const revealFromEvent = (event?: Event): void => {
    const url = getUrl();
    href.value = url;

    // Direct activation (programmatic `.click()`, keyboard Enter, direct
    // auxclick) performs the anchor's default action right after event
    // dispatch, before the queued (microtask) Vue DOM patch can apply
    // `href.value`. Write the attribute on the event target synchronously so
    // the default action always sees it.
    const target = event?.currentTarget;
    // `Element` is a browser-only global: guard it so this public handler
    // never throws in SSR/Node contexts (the guard short-circuits before
    // `instanceof` evaluates there).
    if (typeof Element !== 'undefined' && target instanceof Element) {
      target.setAttribute('href', url);
    }
  };

  const intentHandlers: LazyHrefIntentHandlers = {
    onMouseenter: revealFromEvent,
    onFocus: revealFromEvent,
    onPointerdown: revealFromEvent,
    onTouchstart: revealFromEvent,
    onContextmenu: revealFromEvent,
    onAuxclick: revealFromEvent,
    onClick: revealFromEvent,
  };

  return {
    href,
    reveal,
    revealFromEvent,
    intentHandlers,
  };
}
